import { MetricsRouteData, PtXy, XyArray } from 'appTypes';
import {
  isValidDateTimeFormat,
  parseDate,
  parseDateTimeIgnoringTz,
} from 'features/common/datetimeUtils';
import { isComparisonMonth } from 'features/task_bar/ComparisonPicker';

import { convertSpeeds } from '../common/utils';
import { graph_highlight_color } from './chartOptions';

// checks if either target or comparison spans multiple days
export function isMultiDayChart(
  targetDate: string,
  targetEndDate?: string,
  comparison?: string,
  comparisonEndDate?: string,
): boolean {
  const targetIsRange = !!targetEndDate && targetEndDate !== targetDate;
  const compIsDay = !!comparison && !isComparisonMonth(comparison);
  const compIsRange =
    !!comparisonEndDate && compIsDay && comparisonEndDate !== comparison;
  return targetIsRange || compIsRange;
}

// Correct way of handling the timezones, however highcharts isn't obeying my timezone proclamation
// import utc from 'dayjs/plugin/utc';
// import timezone from 'dayjs/plugin/timezone';
// import customParseFormat from 'dayjs/plugin/customParseFormat';
// dayjs.extend(utc);
// dayjs.extend(timezone);
// dayjs.extend(customParseFormat);
// function chartStart(YYYY_MM_DD: string) {
//   return dayjs.tz(YYYY_MM_DD, 'YYYY-MM-DD', timezoneNY).subtract(3, 'hour');
// }
// function chartEnd(YYYY_MM_DD: string) {
//   return dayjs(YYYY_MM_DD, 'YYYY-MM-DD')
//     .tz(timezoneNY, true).add(1, 'day').add(3, 'hour');
// }
// function parse_ts(dt: string) {
//   return dayjs(dt).tz(timezoneNY, false).valueOf();
// }

function chartStart(YYYY_MM_DD: string) {
  return parseDate(YYYY_MM_DD).subtract(3, 'hour');
}

function chartEnd(YYYY_MM_DD: string) {
  return parseDate(YYYY_MM_DD).add(1, 'day').add(3, 'hour');
}

export function parse_ts(dt: string) {
  const dt_t = parseDateTimeIgnoringTz(dt);
  return dt_t.valueOf();
}

function toIndex(timestamp: number, firstBin, lastBin, binTicksMs) {
  // return ordinal bin index for specified timestamp (must be on a bin boundary)
  console.assert(timestamp >= firstBin, timestamp);
  console.assert(timestamp <= lastBin, timestamp);
  const i = (timestamp - firstBin) / binTicksMs;
  console.assert(i % 1 === 0);
  return Math.floor(i);
}

export function sparseToDense(
  sparseArray,
  firstBin,
  lastBin,
  binTicksMs,
  offsetTo,
  row_to_xy: (row: Array<any>) => PtXy,
): XyArray {
  const timeDiff = lastBin - firstBin;
  const numBins = 1 + timeDiff / binTicksMs;

  // console.log(
  //   `numBins ${numBins} with ticks as ${binTicksMs / 1000}, start ${firstBin} end ${lastBin}`,
  // );

  const dense = Array(numBins);
  for (let i = 0; i < numBins; i += 1) {
    const b = firstBin + i * binTicksMs;
    dense[i] = { x: b, y: null };
  }

  const sparseMetrics = sparseArray.map(row_to_xy);
  // console.log(sparseMetrics);
  for (let i = 0; i < sparseMetrics.length; i += 1) {
    const { x } = sparseMetrics[i];
    // drop datapoints outside [firstBin, lastBin] - multiday case
    if (x < firstBin || x > lastBin) continue;
    const index = toIndex(x, firstBin, lastBin, binTicksMs);
    if (index >= 0 && index < dense.length) {
      dense[index] = sparseMetrics[i];
    }
  }

  for (let i = 0; i < dense.length; i++) {
    // marker for isolated points  (replace && with || below to also put markers at start and end of segments)
    const enabled =
      (i === 0 || dense[i - 1].y === null) &&
      (i === dense.length - 1 || dense[i + 1].y === null);
    dense[i].marker = { enabled };
    dense[i].x += offsetTo - firstBin;
  }
  return dense;
}

// trim the longer one to the length of the shorter one - multiday case
function alignArrays(a: XyArray, b: XyArray) {
  if (!a.length || !b.length) return [[], []];

  const startX = Math.max(a[0].x, b[0].x);
  const endX = Math.min(a[a.length - 1].x, b[b.length - 1].x);

  const aTrim = a.filter((p) => p.x >= startX && p.x <= endX);
  const bTrim = b.filter((p) => p.x >= startX && p.x <= endX);

  return [aTrim, bTrim];
}

export class ChartDataExpander {
  targetDate: string;
  firstBin: number;
  lastBin: number;
  numBins: number;
  binTicksMs: number;
  offsetTo: number;
  // used to limit the chart to a single day - multiday case
  isMultiDay: boolean;

  constructor(
    targetDate: string,
    comparison?: string,
    endDate?: string,
    comparisonEndDate?: string,
  ) {
    console.assert(targetDate);

    if (endDate && isValidDateTimeFormat(endDate)) {
      // Route monitoring usecase with datetime start and end
      // In this case comparison cannot be day, only month
      this.targetDate = targetDate;
      this.firstBin = parseDateTimeIgnoringTz(targetDate).valueOf();
      this.lastBin = parseDateTimeIgnoringTz(endDate).valueOf();
      this.offsetTo = parseDateTimeIgnoringTz(targetDate).valueOf();
      this.isMultiDay = false;
      return;
    }

    this.isMultiDay = isMultiDayChart(
      targetDate,
      endDate,
      comparison,
      comparisonEndDate,
    );

    const onDayComparison = !!comparison && !isComparisonMonth(comparison);
    const seriesStart = onDayComparison ? comparison : targetDate;
    this.targetDate = seriesStart;

    if (this.isMultiDay) {
      //for multidays we use midnight to midnight range
      this.firstBin = parseDate(seriesStart).valueOf();
      this.lastBin = parseDate(seriesStart).add(1, 'day').valueOf();
    } else {
      //for single day we use start of day to end of day +- 3 hours
      this.firstBin = chartStart(seriesStart).valueOf();
      this.lastBin = chartEnd(seriesStart).valueOf();
    }

    if (onDayComparison) {
      this.offsetTo = this.isMultiDay
        ? parseDate(targetDate).valueOf()
        : chartStart(targetDate).valueOf();
    } else {
      this.offsetTo = this.firstBin;
    }
  }

  expandSpeeds(metrics: MetricsRouteData, uses_metric: boolean): XyArray {
    // extract and backfill sparse MetricsRouteData array with null datapoints for all time bins
    // Infer bin size from first row bin size
    if (metrics) {
      console.assert(metrics.bin_size !== undefined, metrics);
      this.binTicksMs = metrics.bin_size * 60 * 1000;
    } else {
      return undefined;
    }
    if (metrics?.speeds) {
      const denseSpeeds = sparseToDense(
        metrics.speeds,
        this.firstBin,
        this.lastBin,
        this.binTicksMs,
        this.offsetTo,
        (row) => {
          return { x: parse_ts(row[0]), y: row[1] };
        },
      );
      if (uses_metric) {
        for (let i = 0; i < denseSpeeds.length; i++) {
          denseSpeeds[i].y = convertSpeeds(denseSpeeds[i].y, uses_metric);
        }
      }
      return denseSpeeds;
    }
    return undefined;
  }

  slowdownBands(
    observed: XyArray,
    typical: XyArray,
    slowdownThreshold: number,
    highlight_higher = false,
  ) {
    // return plotBand array with a band for each interval where observed speed is below slowdownThreshold of typical
    const plotBands = Array(0);

    if (observed && typical && slowdownThreshold) {
      if (observed.length !== typical.length) {
        [observed, typical] = alignArrays(observed, typical);
      }
      let fromX = null;
      let toX = null;
      for (let i = 0; i < observed.length; i += 1) {
        const { x, y: yObserved } = observed[i];
        const { x: xTypical, y: yTypical } = typical[i];

        console.assert(x === xTypical, 'x !== xTypical');
        let isBelow;
        if (yTypical && yObserved) {
          if (highlight_higher) {
            isBelow = yObserved > yTypical * slowdownThreshold;
          } else {
            isBelow = yObserved < yTypical * slowdownThreshold;
          }
        }

        if (isBelow) {
          if (!fromX) {
            // entering band
            fromX = x;
            // console.log(`enter band at ${x}`);
          }
          toX = x;
        } else {
          if (fromX) {
            // leaving band
            const raw_from = fromX;
            const raw_to = toX + this.binTicksMs;
            fromX -= this.binTicksMs / 2; // expand band to bin midpoint before and after
            toX += this.binTicksMs / 2;
            plotBands.push({
              color: graph_highlight_color,
              opacity: 0.5,
              from: fromX,
              raw_from,
              to: toX,
              raw_to,
            });
            // console.log(`leaving band at ${x}`);
          }
          fromX = null;
          toX = null;
        }
      }

      if (fromX) {
        const raw_from = fromX;
        const raw_to = toX + this.binTicksMs;
        fromX -= this.binTicksMs / 2;
        plotBands.push({
          color: graph_highlight_color,
          opacity: 0.5,
          from: fromX,
          raw_from,
          to: toX,
          raw_to,
        });
      }
    }
    return plotBands;
  }

  flatline(y: number): XyArray {
    // return 2-element flatline array with datapoints at first and last time bin
    const bins = [];
    bins.push({ x: this.firstBin, y });
    bins.push({ x: this.lastBin, y });
    return bins;
  }
}
